import type { Address } from "viem"
import type { Config } from "wagmi"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import { LockVotedEvent } from "@/domains/lock/events"
import { EventEmitter, formatCurrency } from "@/domains/library"
import { TransactionReturnType, voteMultiple } from "@/domains/on-chain"

interface VoteInteractionParams {
  account: Address
  chainId: number
  config: Config
  superVoterAddress: Address
  nftIds: Array<number>
  votePools: Array<Address>
  voteCounts: Array<string>
  totalVotesVoting: number
  maxLock: boolean
  emitter: EventEmitter<EmitEvent>
  eventBus: IEventBus
}

export const superVoteMultipleInteraction = async ({
  account,
  chainId,
  config,
  superVoterAddress,
  nftIds,
  votePools,
  voteCounts,
  totalVotesVoting,
  maxLock,
  emitter,
  eventBus,
}: VoteInteractionParams): Promise<TransactionReturnType> => {
  const voted = await voteMultiple({
    account,
    chainId,
    config,
    superVoterAddress,
    nftIds,
    votePools,
    voteCounts,
    maxLock,
  })

  if (voted.success) {
    // Publish domain event for immediate lock refresh and deselection
    eventBus.publish(new LockVotedEvent(nftIds, voted.tx))

    const nftIdsString = nftIds.slice(0, -1).join(", ") + (nftIds.length > 1 ? " & " : "") + nftIds[nftIds.length - 1]
    emitter.emit("super-vote-multiple", {
      title: "SuperVoted",
      description: `SuperVoted ${votePools.length} pools using ${formatCurrency(totalVotesVoting, 0)} votes from ${nftIds.length} NFTs.\n ${nftIdsString}.`,
      variant: "success",
    })
    return voted
  }

  emitter.emit("error-super-voting", {
    title: "Super Vote failed",
    description: `Unable to super vote with multiple NFTs ${nftIds.join(", ")}, reason ${voted.error}`,
    variant: "error",
  })
  return voted
}
